import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { mocApi } from '../api/client';
import { fmtDate, StatusBadge, AlertBadge, CategoryBadge } from '../lib/mocUtils';
import { STATUS_LABELS } from '../api/demoData';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { MagnifyingGlass, CaretUp, CaretDown, FolderOpen } from '@phosphor-icons/react';
import { useSiteFilter } from '../lib/useSites';

const PAGE_SIZE = 8;

export default function MocList() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [all, setAll] = useState(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState(params.get('category') || 'all');
  const [nature, setNature] = useState(params.get('nature') || 'all');
  const [sort, setSort] = useState({ key: 'updatedAt', dir: 'desc' });
  const [page, setPage] = useState(1);
  const scope = params.get('scope');
  const { sites: adminSites, isNational, site: siteFilter, setSite: setSiteFilter } = useSiteFilter();
  const [sidCounts, setSidCounts] = useState({});

  useEffect(() => { mocApi.listMocs().then(setAll); }, []);
  useEffect(() => { mocApi.getSidCounts().then(setSidCounts); }, []);
  useEffect(() => { setPage(1); }, [q, status, category, nature, siteFilter]);

  const filtered = useMemo(() => {
    if (!all) return [];
    let items = [...all];
    if (scope === 'open') items = items.filter((m) => !['cloturee', 'annulee'].includes(m.status));
    if (scope === 'old') items = items.filter((m) => m.ageDays >= 60);
    if (q) { const s = q.toLowerCase(); items = items.filter((m) => m.title.toLowerCase().includes(s) || m.id.toLowerCase().includes(s)); }
    if (status !== 'all') items = items.filter((m) => m.status === status);
    if (category !== 'all') items = items.filter((m) => String(m.category) === category);
    if (nature !== 'all') items = items.filter((m) => m.nature === nature);
    if (siteFilter !== 'all') items = items.filter((m) => m.site === siteFilter);
    items.sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      const cmp = av > bv ? 1 : av < bv ? -1 : 0;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return items;
  }, [all, q, status, category, nature, sort, scope, siteFilter]);

  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const loading = all === null;

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }));
  const SortH = ({ k, children }) => (
    <button data-testid={`sort-${k}`} onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 hover:text-primary">
      {children}{sort.key === k && (sort.dir === 'asc' ? <CaretUp size={12} /> : <CaretDown size={12} />)}
    </button>
  );

  const title = scope === 'mine' ? 'Mes MOC' : scope === 'open' ? 'MOC ouvertes' : scope === 'old' ? 'MOC anciennes' : 'Liste des MOC';

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Registre opérationnel</div>
          <h1 className="font-display text-3xl font-black tracking-tight mt-1">{title}</h1>
        </div>
        <span className="text-sm text-muted-foreground">{loading ? '…' : `${filtered.length} résultat(s)`}</span>
      </div>

      <Card className="rounded-sm p-3 flex flex-col md:flex-row gap-3">
        {isNational && adminSites.length > 0 && (
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger data-testid="filter-site" className="w-full md:w-48 h-9"><SelectValue placeholder="Site" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les sites</SelectItem>
              {adminSites.map((s) => <SelectItem key={s.Site_ID} value={s.Site_ID}>{s.Nom_Site || s.Site_ID}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="moc-search" placeholder="Rechercher (titre ou n° MOC)…" value={q}
            onChange={(e) => setQ(e.target.value)} className="pl-9 h-9" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="filter-status" className="w-full md:w-44 h-9"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="filter-category" className="w-full md:w-36 h-9"><SelectValue placeholder="Catégorie" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Catégories</SelectItem>
            <SelectItem value="1">Catégorie 1</SelectItem>
            <SelectItem value="2">Catégorie 2</SelectItem>
            <SelectItem value="3">Catégorie 3</SelectItem>
          </SelectContent>
        </Select>
        <Select value={nature} onValueChange={setNature}>
          <SelectTrigger data-testid="filter-nature" className="w-full md:w-36 h-9"><SelectValue placeholder="Nature" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Nature</SelectItem>
            <SelectItem value="Permanent">Permanent</SelectItem>
            <SelectItem value="Temporaire">Temporaire</SelectItem>
            <SelectItem value="Urgent">Urgent</SelectItem>
          </SelectContent>
        </Select>
      </Card>

      {/* Desktop table */}
      <Card className="rounded-sm p-0 overflow-hidden hidden md:block">
        <table className="w-full text-sm moc-table">
          <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="text-left font-semibold px-4 py-3"><SortH k="id">N° MOC</SortH></th>
              <th className="text-left font-semibold px-4 py-3"><SortH k="title">Titre</SortH></th>
              <th className="text-left font-semibold px-4 py-3">Catégorie</th>
              <th className="text-left font-semibold px-4 py-3">Statut</th>
              <th className="text-left font-semibold px-4 py-3">Alerte</th>
              <th className="text-left font-semibold px-4 py-3"><SortH k="updatedAt">MAJ</SortH></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`} data-testid="moc-row-skeleton">
                  {Array.from({ length: 6 }).map((__, j) => (
                    <td key={j} className="px-4 py-3"><div className="h-4 bg-muted rounded animate-pulse" /></td>
                  ))}
                </tr>
              ))
              : pageItems.map((m) => (
                <tr key={m.id} data-testid={`moc-row-${m.id}`} onClick={() => navigate(`/moc/${m.id}`)}
                  className="cursor-pointer hover:bg-muted/40">
                  <td className="px-4 py-3 font-mono text-xs">{m.id}</td>
                  <td className="px-4 py-3 font-medium max-w-xs truncate">
                    {m.title}
                    {sidCounts[m.id] > 0 && (
                      <span data-testid={`sid-badge-${m.id}`} role="button" title="Voir les points Safety In Design non conformes"
                        onClick={(e) => { e.stopPropagation(); navigate(`/moc/${m.id}?tab=safety&sid=nc`); }}
                        className="ml-2 inline-flex items-center rounded-full bg-rose-100 text-rose-700 hover:bg-rose-600 hover:text-white cursor-pointer text-[11px] font-bold px-2 py-0.5 align-middle transition-colors">
                        {sidCounts[m.id]} SiD
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3"><CategoryBadge category={m.category} /></td>
                  <td className="px-4 py-3"><StatusBadge status={m.status} /></td>
                  <td className="px-4 py-3"><AlertBadge level={m.alert} /></td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(m.updatedAt)}</td>
                </tr>
              ))}
          </tbody>
        </table>
        {!loading && pageItems.length === 0 && <EmptyState />}
      </Card>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading && Array.from({ length: 4 }).map((_, i) => (
          <Card key={`skm-${i}`} className="rounded-sm p-4"><div className="h-4 w-24 bg-muted rounded animate-pulse" /><div className="h-4 w-3/4 bg-muted rounded animate-pulse mt-2" /></Card>
        ))}
        {!loading && pageItems.map((m) => (
          <Card key={m.id} data-testid={`moc-card-${m.id}`} onClick={() => navigate(`/moc/${m.id}`)}
            className="rounded-sm p-4 cursor-pointer">
            <div className="flex items-center gap-2"><span className="font-mono text-xs text-muted-foreground">{m.id}</span><CategoryBadge category={m.category} /></div>
            <div className="font-semibold mt-1">
              {m.title}
              {sidCounts[m.id] > 0 && (
                <span data-testid={`sid-badge-m-${m.id}`} role="button"
                  onClick={(e) => { e.stopPropagation(); navigate(`/moc/${m.id}?tab=safety&sid=nc`); }}
                  className="ml-2 inline-flex items-center rounded-full bg-rose-100 text-rose-700 text-[11px] font-bold px-2 py-0.5 align-middle">{sidCounts[m.id]} SiD</span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-3"><StatusBadge status={m.status} /><AlertBadge level={m.alert} /></div>
          </Card>
        ))}
        {!loading && pageItems.length === 0 && <Card className="rounded-sm"><EmptyState /></Card>}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" data-testid="page-prev" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Précédent</Button>
          <span className="text-sm text-muted-foreground">Page {page} / {totalPages}</span>
          <Button variant="outline" size="sm" data-testid="page-next" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>Suivant</Button>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="py-16 text-center text-muted-foreground">
      <FolderOpen size={40} className="mx-auto opacity-40" />
      <p className="mt-3 text-sm">Aucune MOC ne correspond aux critères.</p>
    </div>
  );
}
